/**
 * ToolsConfigPopup - Configure AI Tools and MCP Servers
 * 
 * This popup provides:
 * - Toggle individual tools on/off
 * - Configure MCP server connections
 * - View tool descriptions and capabilities
 * - Search and filter tools
 */

import React, { memo, useCallback, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import {
    Button,
    PopupWrapper,
    TextLabel,
    BsPlus,
    BiTrash,
    BsCheck2Circle,
    BsXCircle,
    BiSearch,
    BiX,
    BsTools,
    BsGlobe,
    BsLightningCharge,
} from '@acme/ui';
import { toolMetadata, type ToolName } from '@acme/util';
import {
    globalToolsConfigAtom,
    mcpServersConfigAtom,
    modelPopupAtom,
    type MCPServerUIConfig,
} from '../store/instruction';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ToolsConfigPopup = memo(() => {
    const [popup, setPopup] = useAtom(modelPopupAtom);
    const [toolsConfig, setToolsConfig] = useAtom(globalToolsConfigAtom);
    const [mcpServers, setMcpServers] = useAtom(mcpServersConfigAtom);
    const [activeTab, setActiveTab] = useState<'tools' | 'mcp'>('tools');
    const [searchQuery, setSearchQuery] = useState('');

    const isOpen = popup.open && popup.type === 'tools-config';

    const handleClose = useCallback(() => {
        setPopup({ open: false, type: '' });
        setSearchQuery('');
    }, [setPopup]);

    const toggleTool = useCallback((toolName: string) => {
        setToolsConfig(prev => {
            const enabledTools = prev.enabledTools;
            const isEnabled = enabledTools.includes(toolName);
            return {
                ...prev,
                enabledTools: isEnabled
                    ? enabledTools.filter((t: string) => t !== toolName)
                    : [...enabledTools, toolName],
            };
        });
    }, [setToolsConfig]);

    const enableAllTools = useCallback(() => {
        const allEnabledTools = Object.entries(toolMetadata)
            .filter(([_, meta]) => meta.enabled)
            .map(([name]) => name);
        setToolsConfig(prev => ({ ...prev, enabledTools: allEnabledTools }));
    }, [setToolsConfig]);

    const disableAllTools = useCallback(() => {
        setToolsConfig(prev => ({ ...prev, enabledTools: [] }));
    }, [setToolsConfig]);

    const enabledToolsArray = toolsConfig.enabledTools;

    if (!isOpen) return null;

    return (
        <PopupWrapper
            show={isOpen}
            onClose={handleClose}
            className="!bg-transparent !p-0 !shadow-none"
            containerClassName="z-[100002]"
            noClose
        >
            <div className="w-full max-w-3xl bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-neutral-700">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                                <BsTools className="text-white text-lg" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Tools & Extensions
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Extend your AI with powerful capabilities
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                        >
                            <BiX className="text-xl text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-neutral-700 px-6">
                    <button
                        onClick={() => setActiveTab('tools')}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === 'tools'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        <BsLightningCharge className="text-base" />
                        Built-in Tools
                        <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                            {enabledToolsArray.length}
                        </span>
                        {activeTab === 'tools' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('mcp')}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === 'mcp'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        <BsGlobe className="text-base" />
                        MCP Servers
                        {mcpServers.length > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400">
                                {mcpServers.length}
                            </span>
                        )}
                        {activeTab === 'mcp' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                        )}
                    </button>
                </div>

                {/* Search (only for tools tab) */}
                {activeTab === 'tools' && (
                    <div className="px-6 py-3 border-b border-gray-100 dark:border-neutral-800">
                        <div className="relative">
                            <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search tools..."
                                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <BiX className="text-lg" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="p-6 max-h-[50vh] overflow-y-auto">
                    {activeTab === 'tools' ? (
                        <ToolsTab
                            enabledTools={enabledToolsArray}
                            onToggle={toggleTool}
                            onEnableAll={enableAllTools}
                            onDisableAll={disableAllTools}
                            searchQuery={searchQuery}
                        />
                    ) : (
                        <MCPTab
                            servers={mcpServers}
                            onUpdate={setMcpServers}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800/50 flex justify-between items-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {activeTab === 'tools'
                            ? `${enabledToolsArray.length} of ${Object.keys(toolMetadata).length} tools enabled`
                            : `${mcpServers.filter(s => s.enabled).length} of ${mcpServers.length} servers connected`
                        }
                    </p>
                    <Button
                        onClick={handleClose}
                        className="!bg-blue-600 hover:!bg-blue-700 !text-white px-6 !rounded-xl"
                    >
                        Done
                    </Button>
                </div>
            </div>
        </PopupWrapper>
    );
});

ToolsConfigPopup.displayName = 'ToolsConfigPopup';

// =============================================================================
// TOOLS TAB
// =============================================================================

interface ToolsTabProps {
    enabledTools: string[];
    onToggle: (toolName: string) => void;
    onEnableAll: () => void;
    onDisableAll: () => void;
    searchQuery: string;
}

const ToolsTab = memo(({ enabledTools, onToggle, onEnableAll, onDisableAll, searchQuery }: ToolsTabProps) => {
    // Filter and group tools by category
    const { groupedTools, totalFiltered } = useMemo(() => {
        const filtered = Object.entries(toolMetadata).filter(([name, meta]) => {
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase();
            return (
                name.toLowerCase().includes(query) ||
                meta.name.toLowerCase().includes(query) ||
                meta.description.toLowerCase().includes(query)
            );
        });

        const grouped = filtered.reduce((acc, [name, meta]) => {
            const category = meta.category;
            if (!acc[category]) {
                acc[category] = [];
            }
            (acc[category] as Array<{ toolKey: string } & typeof toolMetadata[ToolName]>).push({ ...meta, toolKey: name });
            return acc;
        }, {} as Record<string, Array<{ toolKey: string } & typeof toolMetadata[ToolName]>>);

        return { groupedTools: grouped, totalFiltered: filtered.length };
    }, [searchQuery]);

    const categoryInfo: Record<string, { label: string; icon: string; color: string }> = {
        compute: { label: 'Compute & Math', icon: '🧮', color: 'blue' },
        utility: { label: 'Utilities', icon: '🔧', color: 'green' },
        search: { label: 'Search & Web', icon: '🔍', color: 'purple' },
        data: { label: 'Data & Analysis', icon: '📊', color: 'orange' },
    };

    if (totalFiltered === 0) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center">
                    <BiSearch className="text-2xl text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400">No tools found for &quot;{searchQuery}&quot;</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Quick actions */}
            <div className="flex items-center justify-between">
                <div className="flex gap-2">
                    <button
                        onClick={onEnableAll}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                    >
                        Enable all
                    </button>
                    <button
                        onClick={onDisableAll}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    >
                        Disable all
                    </button>
                </div>
            </div>

            {/* Tool categories */}
            {Object.entries(groupedTools).map(([category, tools]) => {
                const info = categoryInfo[category] || { label: category, icon: '🔧', color: 'gray' };

                return (
                    <div key={category} className="space-y-3">
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                            <span>{info.icon}</span>
                            <span>{info.label}</span>
                            <span className="text-xs font-normal text-gray-400">({tools.length})</span>
                        </h3>
                        <div className="grid gap-2">
                            {tools.map(tool => {
                                const isEnabled = enabledTools.includes(tool.toolKey);
                                const isAvailable = tool.enabled;

                                return (
                                    <div
                                        key={tool.toolKey}
                                        onClick={() => isAvailable && onToggle(tool.toolKey)}
                                        className={`
                                            group flex items-center justify-between p-3.5 rounded-xl
                                            border transition-all cursor-pointer
                                            ${isEnabled
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                                                : 'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 hover:border-gray-300 dark:hover:border-neutral-600'
                                            }
                                            ${!isAvailable ? 'opacity-60 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`
                                                w-10 h-10 rounded-lg flex items-center justify-center text-lg
                                                ${isEnabled
                                                    ? 'bg-blue-100 dark:bg-blue-800/50'
                                                    : 'bg-gray-100 dark:bg-neutral-700'
                                                }
                                            `}>
                                                {tool.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium ${isEnabled
                                                        ? 'text-blue-900 dark:text-blue-100'
                                                        : 'text-gray-900 dark:text-white'
                                                    }`}>
                                                    {tool.name}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                                    {tool.description}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-3">
                                            {!isAvailable && (
                                                <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-lg whitespace-nowrap">
                                                    Coming Soon
                                                </span>
                                            )}
                                            {isAvailable && (
                                                <div className={`
                                                    relative w-11 h-6 rounded-full transition-colors
                                                    ${isEnabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-neutral-600'}
                                                `}>
                                                    <div className={`
                                                        absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform
                                                        ${isEnabled ? 'translate-x-6' : 'translate-x-1'}
                                                    `} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

ToolsTab.displayName = 'ToolsTab';

// =============================================================================
// MCP TAB
// =============================================================================

interface MCPTabProps {
    servers: MCPServerUIConfig[];
    onUpdate: (servers: MCPServerUIConfig[]) => void;
}

const MCPTab = memo(({ servers, onUpdate }: MCPTabProps) => {
    const [newServer, setNewServer] = useState<Partial<MCPServerUIConfig>>({
        name: '',
        url: '',
        type: 'http',
    });
    const [isAdding, setIsAdding] = useState(false);

    const handleAddServer = useCallback(() => {
        if (!newServer.name || !newServer.url) return;

        const server: MCPServerUIConfig = {
            id: `mcp-${Date.now()}`,
            name: newServer.name,
            url: newServer.url,
            type: (newServer.type as 'http' | 'sse') || 'http',
            enabled: true,
        };

        onUpdate([...servers, server]);
        setNewServer({ name: '', url: '', type: 'http' });
        setIsAdding(false);
    }, [newServer, servers, onUpdate]);

    const toggleServer = useCallback((id: string) => {
        onUpdate(servers.map(s =>
            s.id === id ? { ...s, enabled: !s.enabled } : s
        ));
    }, [servers, onUpdate]);

    const removeServer = useCallback((id: string) => {
        onUpdate(servers.filter(s => s.id !== id));
    }, [servers, onUpdate]);

    return (
        <div className="space-y-4">
            {/* Info banner */}
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                        <BsGlobe className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Model Context Protocol (MCP)
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Connect to MCP servers to add custom tools, data sources, and capabilities to your AI assistant.
                        </p>
                    </div>
                </div>
            </div>

            {/* Server list */}
            {servers.length > 0 ? (
                <div className="space-y-2">
                    {servers.map(server => (
                        <div
                            key={server.id}
                            className={`
                                flex items-center justify-between p-4 rounded-xl border transition-all
                                ${server.enabled
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : 'bg-gray-50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700'
                                }
                            `}
                        >
                            <div className="flex items-center gap-3">
                                {server.enabled ? (
                                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-800/50 flex items-center justify-center">
                                        <BsCheck2Circle className="text-green-600 dark:text-green-400" />
                                    </div>
                                ) : (
                                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-neutral-700 flex items-center justify-center">
                                        <BsXCircle className="text-gray-400" />
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {server.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                        {server.url}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => toggleServer(server.id)}
                                    className={`
                                        px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                                        ${server.enabled
                                            ? 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                            : 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50'
                                        }
                                    `}
                                >
                                    {server.enabled ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                    onClick={() => removeServer(server.id)}
                                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                >
                                    <BiTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !isAdding && (
                <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center">
                        <BsGlobe className="text-2xl text-gray-400" />
                    </div>
                    <p className="text-gray-900 dark:text-white font-medium">No MCP servers configured</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Add a server to extend your AI&apos;s capabilities
                    </p>
                </div>
            )}

            {/* Add new server form */}
            {isAdding ? (
                <div className="p-5 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10 space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Add New Server</h4>

                    <div className="space-y-3">
                        <div>
                            <TextLabel size="sm" className="text-gray-700 dark:text-gray-300">Server Name</TextLabel>
                            <input
                                type="text"
                                value={newServer.name || ''}
                                onChange={e => setNewServer(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="My MCP Server"
                                className="w-full mt-1.5 px-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <TextLabel size="sm" className="text-gray-700 dark:text-gray-300">Server URL</TextLabel>
                            <input
                                type="text"
                                value={newServer.url || ''}
                                onChange={e => setNewServer(prev => ({ ...prev, url: e.target.value }))}
                                placeholder="https://mcp.example.com/api"
                                className="w-full mt-1.5 px-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <TextLabel size="sm" className="text-gray-700 dark:text-gray-300">Transport Type</TextLabel>
                            <select
                                value={newServer.type || 'http'}
                                onChange={e => setNewServer(prev => ({ ...prev, type: e.target.value as 'http' | 'sse' }))}
                                className="w-full mt-1.5 px-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                            >
                                <option value="http">HTTP</option>
                                <option value="sse">SSE (Server-Sent Events)</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                        <Button
                            onClick={handleAddServer}
                            disabled={!newServer.name || !newServer.url}
                            className="!bg-blue-600 hover:!bg-blue-700 !text-white flex-1 !rounded-xl disabled:opacity-50"
                        >
                            Add Server
                        </Button>
                        <Button
                            onClick={() => {
                                setIsAdding(false);
                                setNewServer({ name: '', url: '', type: 'http' });
                            }}
                            className="!bg-gray-200 dark:!bg-neutral-700 !text-gray-700 dark:!text-gray-300 !rounded-xl"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={() => setIsAdding(true)}
                    className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-neutral-600 text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all"
                >
                    <BsPlus className="text-xl" />
                    <span className="text-sm font-medium">Add MCP Server</span>
                </button>
            )}
        </div>
    );
});

MCPTab.displayName = 'MCPTab';

export default ToolsConfigPopup;
